import { TTopExperiencesReponse } from "domain/entities/top-experience-response";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { HighlightsContext } from "./HighLightsProvider";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { checkParamsFromPortfolio } from "helper/filtersHelper";

type TTopExperiencesProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource?: IReportsRepository;
};

export type TopExperienceProviderState = {
  requestParamsIndex: number;
  topExperienceData?: TTopExperiencesReponse;
  setValue?: (name: keyof TTopExperienceFilterState, value: any) => void;
  topExperienceFilter?: TTopExperienceFilterState;
  isLoading: boolean;
};

type TFilterSelectOption = {
  label: string | undefined;
  value: string | number | undefined;
};

export type TTopExperienceFilterState = {
  sources?: TFilterSelectOption;
  metrics?: TFilterSelectOption;
};

const initContext: TopExperienceProviderState = {
  requestParamsIndex: 0,
  isLoading: true,
};
export const TopExperienceContext = createContext(initContext);

export const TopExperienceProvider = ({
  requestParamsIndex,
  reportsDatasource,
  children,
}: TTopExperiencesProps) => {
  const { sources, metrics } = useContext(HighlightsContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [topExperienceData, setTopExperienceData] =
    useState<TTopExperiencesReponse>();
  const [topExperienceFilter, setTopExperienceFilter] =
    useState<TTopExperienceFilterState>({
      sources: {
        label: "Campaign",
        value: 1,
      },
      metrics: {
        label: "Visitors",
        value: 1,
      },
    });

  const setValue = (name: keyof TTopExperienceFilterState, value?: any) => {
    setTopExperienceFilter((old) => ({ ...old, [name]: value }));
  };

  useEffect(() => {
    if (sources && metrics) {
      setTopExperienceFilter({
        sources: {
          label: sources[0].label,
          value: sources[0].value,
        },
        metrics: {
          label: metrics[0].label,
          value: metrics[0].value,
        },
      });
    }
  }, [sources, metrics]);

  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const topExperienceRequestParams = useMemo(
    () => ({
      ...currentRequestParams,
      TypeExperienceId: topExperienceFilter.sources?.value,
      TypeOrderId: topExperienceFilter.metrics?.value,
    }),
    [topExperienceFilter.sources?.value, topExperienceFilter.metrics?.value]
  );

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsFromPortfolio);
    }

    return checkParamsFromPortfolio(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        ?.getTopExperience(topExperienceRequestParams)
        .then(setTopExperienceData)
        .then(() => setIsLoading(false));
    }
  }, [topExperienceRequestParams]);

  return (
    <TopExperienceContext.Provider
      value={{
        requestParamsIndex,
        topExperienceData,
        setValue,
        topExperienceFilter,
        isLoading,
      }}
    >
      {children}
    </TopExperienceContext.Provider>
  );
};
